import React, { useEffect } from 'react';

import { Box } from '@mui/system';
import { Grid } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminResetValue,
  enableChat,
  getAdminInfo,
  getAdminPicture,
  getAdminProfile,
  getAdminSettings,
  getAllBannerLogos,
  resetStatus,
} from '@pharmaplan/common';
import PrimaryToolbar from '../AdminToolbar/PrimaryToolbar';
import SecondaryToolbar from '../AdminToolbar/SecondaryToolbar';
import { navigationConfig } from '../AdminToolbar/config';
import {
  Constants,
  PrimaryAdminNavigation,
  SecondaryAdminNavigation,
} from '../../../helpers/Constants';
import useHandleBookingCancel from '../../../hooks/useHandleBookingCancel';
import Navbar from '../../Navbar';
import Drawer from '../../Drawer';

import styles from './styles';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import {
  enableAddCalendarEvent,
  setMultipleAddEvents,
} from '../../../reducers/mainCalendarReducer';
import useAdminPermissions from '../../../hooks/Admin/useAdminPermissions';
import useAdmin from '../../../hooks/useAdmin';
import { setPrimaryRoute } from '../../../actions/navbarActions';
import { useAppSelector } from '../../../hooks/useAppSelector';
import useAdminUnreadChats from '../../../hooks/Chat/useAdminUnreadChats/useAdminUnreadChats';

export interface PrimaryItem {
  key: PrimaryAdminNavigation;
  label: string;
  path: string;
  subPaths: Array<SecondaryAdminNavigation>;
}

const resetValues = ['incompatibleMatches', 'incompatibleMatchesSearch'];
const {
  updateAdmin,
  createMultiple,
  createTechnicianMultiple,
  createStudentMultiple,
} = Constants.paths.admin;

const AdminHome = () => {
  const dispatch = useAppDispatch();
  const { isSuperAdmin } = useAdmin();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { mainContainer, content, contentContainer } = styles;
  const { can } = useAdminPermissions();
  const isChatEnabled = useAppSelector(enableChat);

  const navigationConf = navigationConfig(can, isSuperAdmin, isChatEnabled);

  useHandleBookingCancel();
  useAdminUnreadChats();

  const isIncompUpdate = pathname === Constants.paths.admin.updateIncompatibleMatch;
  const isIncomp = pathname === Constants.paths.admin.incompatibleMatch;
  const shouldKeep = isIncompUpdate || isIncomp;

  useEffect(() => {
    dispatch(setPrimaryRoute(navigationConf[0].key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultClick = (item: PrimaryItem) =>
    () => {
      const { key, path } = item;
      const isChat = key === PrimaryAdminNavigation.chat;
      dispatch(setMultipleAddEvents(false));

      if (isChat) {
        window.open(`${window.location.origin}${path}`, '_blank');
        return;
      }

      dispatch(setPrimaryRoute(key));
      navigate(path);
    };

  const handleReset = () => {
    dispatch(adminResetValue(resetValues));
    dispatch(resetStatus([adminActions.getIncompatibleMatchList]));
  };

  useEffect(() => {
    dispatch(getAdminProfile());
    dispatch(getAdminInfo());
    dispatch(getAdminPicture());
    dispatch(getAllBannerLogos());
    if (pathname !== updateAdmin) {
      dispatch(getAdminSettings());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shouldKeep) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldKeep]);

  useEffect(() => {
    const isMultiplePath = pathname === createMultiple
      || pathname === createTechnicianMultiple
      || pathname === createStudentMultiple;
    dispatch(setMultipleAddEvents(isMultiplePath));
    dispatch(enableAddCalendarEvent(isMultiplePath));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Box sx={mainContainer} component="div">
      <Grid sx={contentContainer} item sm={11}>
        <Drawer />

        {/* TopBar */}
        <Box component="div">
          <Navbar
            UpperToolbar={<PrimaryToolbar defaultClick={defaultClick} />}
            BottomToolbar={<SecondaryToolbar />}
          />
        </Box>
        {/* Content */}
        <Box sx={content}>
          <Outlet />
        </Box>
      </Grid>
    </Box>
  );
};

export default AdminHome;
